import { useEffect, useState } from 'react';

/**
 * How the demonstration is staged.
 * - pinned: one pinned scene, the three visuals transition on a single stage (scrubbed).
 * - flow: no pin, each beat is followed by its own visual, revealed on enter.
 * - static: prefers-reduced-motion. Same layout as flow, everything in its final state.
 */
export type DemoMode = 'pinned' | 'flow' | 'static';

/**
 * The scene only pins when the stage has room for every beat. From 1280px the agent
 * sits in two panes and 660px of height is enough; from 1024px it is stacked and
 * needs 760px. Shorter viewports get the stacked layout instead of a cramped stage.
 */
const MOTION = '(prefers-reduced-motion: no-preference)';
export const PINNED_QUERY = [
	`${MOTION} and (min-width: 1280px) and (min-height: 660px)`,
	`${MOTION} and (min-width: 1024px) and (min-height: 760px)`,
].join(', ');
const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

/** Live media query, read synchronously on the first render (the right layout from the first paint). */
function useMedia(query: string): boolean {
	const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);

	useEffect(() => {
		const mql = window.matchMedia(query);
		const onChange = () => setMatches(mql.matches);
		onChange();
		mql.addEventListener('change', onChange);
		return () => mql.removeEventListener('change', onChange);
	}, [query]);

	return matches;
}

export function useDemoMode(): DemoMode {
	const reduced = useMedia(REDUCED_QUERY);
	const pinned = useMedia(PINNED_QUERY);
	if (reduced) return 'static';
	return pinned ? 'pinned' : 'flow';
}
